// Generates the public form HTML from the builder schema (FORMS_SPEC.md). Pure
// (no React) so it can be unit-tested and used for the editor's live preview.
//
// The output reuses the existing serving/JS pipeline: a <form class="subscribe_form">
// whose named inputs are serialized + POSTed, and a button.subscribe_button the
// injector relabels while sending. The backend parses this HTML as a Go
// html/template, so user text must never carry a literal "{{" (see esc).

// Escapes user text for HTML, plus "{" (Go template delimiters) and "\" (the
// backend embeds the HTML in a JS string literal).
export const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"'{}\\]/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    "{": "&#123;", "}": "&#125;", "\\": "&#92;",
  }[c]));

// Multi-line text: escaped, with line breaks kept.
const escLines = (s) => esc(s).replace(/\r?\n/g, "<br>");

// Only http(s) images; anything else (javascript:, data:, typos) is dropped.
export const safeImageUrl = (u) => {
  const s = String(u || "").trim();
  return /^https?:\/\/[^\s"'<>]+$/i.test(s) ? s : "";
};

const safeColor = (c, fallback) => (/^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(String(c || "")) ? c : fallback);

// The input name the public form posts: custom fields go as cf_<key> (the backend
// routes those into custom_fields); built-ins keep their plain name.
export const inputName = (f) => (f.source === "custom" ? "cf_" + f.key : f.key);

// The default field set for a new builder form.
export function defaultFormFields() {
  return [
    { source: "builtin", key: "email", label: "Email", mode: "visible", required: true, value: "" },
    { source: "builtin", key: "name", label: "Name", mode: "visible", required: false, value: "" },
  ];
}

// What the visitor reads. Every piece is optional; an empty content block
// renders just the fields and a "Subscribe" button.
export function defaultFormContent() {
  return {
    image_url: "", image_file: "", headline: "", subheadline: "", cta_text: "Subscribe", fine_print: "",
    // Shown instead of the form once this browser has signed up (right after
    // submitting, and whenever the form loads again).
    thanks_headline: "Thanks for signing up!", thanks_text: "",
  };
}

// How the form looks.
//   layout:       "card"    bordered box, image on top
//                 "split"   image beside the text/fields, stacks when narrow
//                 "minimal" no box, image on top
//   button_shape: "square" | "rounded" | "pill"
//   button_width: "full" | "auto"
export function defaultFormStyle() {
  return { layout: "card", button_shape: "square", button_width: "full", accent_color: "#0d6db8" };
}

// Where the form shows (form_schema.display; the form script reads it).
//   mode:        "inline" sits where the embed snippet is pasted
//                "popup"  opens in a popup when the mouse leaves the page
//                         (desktop only: phones have no mouse)
//   trigger:     "top" the mouse leaving through the top opens it
//                "any" any edge does
//   quiet_hours: once a visitor closes the popup, it stays closed this many
//                hours (0 = it can open again on their next page, -1 = never)
//   phone:       what opens it on phones and tablets, which have no mouse:
//                "off" nothing, "scroll_up" a quick flick back up the page,
//                "timer" phone_seconds on the page
export function defaultFormDisplay() {
  return { mode: "inline", trigger: "top", quiet_hours: 168, phone: "off", phone_seconds: 30 };
}

// The choices for what opens the popup on phones and tablets.
export const POPUP_PHONE_CHOICES = [
  { label: "Don't show it", value: "off" },
  { label: "Quick scroll back up", value: "scroll_up" },
  { label: "After some time", value: "timer" },
];

// The wait for "After some time", in seconds (the server allows 1 to 600).
export const POPUP_PHONE_SECONDS = { min: 5, max: 600 };

// The choices for how long a closed popup stays closed.
export const POPUP_QUIET_CHOICES = [
  { label: "On their next page", value: 0 },
  { label: "1 hour later", value: 1 },
  { label: "1 day later", value: 24 },
  { label: "1 week later", value: 168 },
  { label: "30 days later", value: 720 },
  { label: "Never", value: -1 },
];

const RADIUS = { square: "4px", rounded: "10px", pill: "999px" };

// Darkens a #rrggbb color by pct (0..1) for the button hover state.
function darken(hex, pct) {
  let h = hex.slice(1);
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  const ch = (shift) => Math.max(0, Math.round(((n >> shift) & 255) * (1 - pct)));
  return "#" + [16, 8, 0].map((s) => ch(s).toString(16).padStart(2, "0")).join("");
}

// opts.showThanks renders the thank-you state (for the editor preview); on a
// live page the form script adds jc_show_thanks once the visitor signs up.
export function buildFormHtml(fields, typeId, content, style, opts = {}) {
  const c = { ...defaultFormContent(), ...(content || {}) };
  const st = { ...defaultFormStyle(), ...(style || {}) };
  const layout = ["card", "split", "minimal"].includes(st.layout) ? st.layout : "card";
  const accent = safeColor(st.accent_color, defaultFormStyle().accent_color);
  const radius = RADIUS[st.button_shape] || RADIUS.square;
  const fullButton = st.button_width !== "auto";
  const image = safeImageUrl(c.image_url);

  const rows = [];
  (fields || []).forEach((f) => {
    const nm = inputName(f);
    if (f.mode === "preset") {
      rows.push(`<input type="hidden" name="${esc(nm)}" value="${esc(f.value)}">`);
      return;
    }
    const required = f.required || f.key === "email";
    const type = f.key === "email" ? "email" : "text";
    rows.push(
      `<div class="jc_field"><label>${esc(f.label)}${required ? " *" : ""}</label>` +
      `<input type="${type}" name="${esc(nm)}"${required ? " required" : ""}></div>`
    );
  });
  if (typeId) rows.push(`<input type="hidden" name="record_type_id" value="${esc(typeId)}">`);
  rows.push(`<input type="text" name="website" class="jc_hp" tabindex="-1" autocomplete="off">`);

  const header = [];
  if (c.headline) header.push(`<h2 class="jc_headline">${esc(c.headline)}</h2>`);
  if (c.subheadline) header.push(`<p class="jc_subheadline">${escLines(c.subheadline)}</p>`);

  const ctaText = esc(c.cta_text || "Subscribe");
  const finePrint = c.fine_print ? `<p class="jc_fine_print">${escLines(c.fine_print)}</p>` : "";
  const imageHtml = image ? `<div class="jc_image"><img src="${esc(image)}" alt=""></div>` : "";

  const W = ".jc_form_wrap";
  const css = [
    `${W}{max-width:${layout === "split" ? 880 : 420}px;margin:20px auto;font-family:"Helvetica Neue",Helvetica,Arial,sans-serif;color:#333;box-sizing:border-box;` +
      (layout === "card" ? "border:1px solid #e2e2e2;border-radius:6px;box-shadow:0 1px 4px rgba(0,0,0,.08);padding:24px;background:#fff;}" : "padding:8px;}"),
    `${W} *{box-sizing:border-box;}`,
    `${W} .jc_image img{display:block;width:100%;height:auto;border-radius:${layout === "card" ? "4px" : "16px"};}`,
    `${W} .jc_headline{margin:0 0 8px;font-size:${layout === "split" ? 36 : 24}px;line-height:1.15;font-weight:700;text-align:center;color:#111;}`,
    `${W} .jc_subheadline{margin:0 0 16px;font-size:${layout === "split" ? 18 : 15}px;line-height:1.4;text-align:center;}`,
    `${W} .jc_field{margin-bottom:12px;}`,
    `${W} label{display:block;font-size:13px;font-weight:600;margin-bottom:4px;}`,
    `${W} input[type=text],${W} input[type=email]{width:100%;font-size:14px;padding:10px 8px;border:1px solid #d6d6d6;border-radius:4px;background:#f8f7f7;}`,
    `${W} input:focus{outline:none;border-color:${accent};}`,
    `${W} .jc_hp{position:absolute!important;left:-9999px!important;}`,
    `${W} .jc_button_row{text-align:center;}`,
    `${W} .subscribe_button{${fullButton ? "width:100%;" : ""}color:#fff;margin-top:8px;padding:12px ${fullButton ? "0" : "48px"};font-size:16px;font-weight:600;letter-spacing:.02em;background:${accent};border:none;border-radius:${radius};cursor:pointer;}`,
    `${W} .subscribe_button:hover{background:${darken(accent, 0.15)};}`,
    `${W} .jc_fine_print{margin:16px 0 0;font-size:12px;line-height:1.45;color:#555;}`,
    `${W} .jc_thanks{display:none;padding:${layout === "split" ? "24px 0" : "8px 0"};}`,
    `${W}.jc_show_thanks .jc_form_body{display:none;}`,
    `${W}.jc_show_thanks .jc_thanks{display:block;}`,
  ];
  if (layout === "split") {
    // Flex-wrap stacks the columns on its own once the embed is too narrow for
    // both, so this works in a sidebar or on a phone without media queries.
    css.push(
      `${W} .jc_split{display:flex;flex-wrap:wrap;align-items:center;gap:32px;}`,
      `${W} .jc_split>.jc_image{flex:1 1 300px;}`,
      `${W} .jc_split>.jc_body{flex:1 1 320px;}`
    );
  } else {
    css.push(`${W} .jc_image{margin-bottom:16px;}`);
  }

  const form =
    '<form class="subscribe_form">\n      ' +
    rows.join("\n      ") +
    `\n      <div class="jc_button_row"><button class="subscribe_button" type="submit">${ctaText}</button></div>` +
    "\n    </form>";

  const formBody = [header.join("\n    "), form, finePrint].filter(Boolean).join("\n    ");
  const thanks =
    `<div class="jc_thanks"><h2 class="jc_headline">${esc(c.thanks_headline || "Thanks for signing up!")}</h2>` +
    (c.thanks_text ? `<p class="jc_subheadline">${escLines(c.thanks_text)}</p>` : "") +
    "</div>";
  const body = `<div class="jc_form_body">\n    ${formBody}\n    </div>\n    ${thanks}`;
  const inner =
    layout === "split" && imageHtml
      ? `<div class="jc_split">\n  ${imageHtml}\n  <div class="jc_body">\n    ${body}\n  </div>\n</div>`
      : [imageHtml, body].filter(Boolean).join("\n  ");

  const wrapClass = `jc_form_wrap jc_layout_${layout}${opts.showThanks ? " jc_show_thanks" : ""}`;
  return `<style>\n${css.join("\n")}\n</style>\n<div class="${wrapClass}">\n  ${inner}\n</div>`;
}
